import { MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { britishEnglish } from '../lib/api';

const breadcrumbRoutes={
  Administration:'/settings',
  Cases:'/cases',
  Commercial:'/billing',
  Complaints:'/complaints',
  Customers:'/customers',
  Orders:'/orders',
};

export function PageHeader({eyebrow,title,description,actions,children}){
  return <header className="page-header govuk-!-margin-bottom-7"><div>{eyebrow&&<span className="eyebrow govuk-caption-l">{eyebrow}</span>}<h1 className="govuk-heading-l">{title}</h1>{description&&<p className="govuk-body">{description}</p>}{children}</div>{actions&&<div className="page-actions govuk-button-group">{actions}</div>}</header>;
}

export function MetricCard({label,value,detail,accent='blue'}){
  return <section className={`metric-card govuk-summary-card accent-${accent}`} aria-label={label}><div className="metric-label govuk-caption-m">{label}</div><strong className="metric-value govuk-heading-l">{value}</strong><div className="metric-detail govuk-body-s">{detail}</div></section>;
}

export function Status({value}){
  const cls=String(value||'').toLowerCase().replaceAll(' ','-').replaceAll('_','-');
  return <strong className={`status govuk-tag status-${cls}`}>{britishEnglish(value)}</strong>;
}

export function EmptyState({title,body}){
  return <div className="empty-state govuk-inset-text" role="status"><strong>{title}</strong><span>{body}</span></div>;
}

export function Card({title,action,children,className=''}){
  return <section className={`panel-card govuk-summary-card govuk-!-margin-bottom-6 ${className}`}><header className="panel-card-header govuk-summary-card__title-wrapper"><h2 className="govuk-summary-card__title govuk-heading-m">{title}</h2>{action}</header>{children}</section>;
}

function initials(name=''){
  return String(name||'?').trim().split(/\s+/).slice(0,2).map(part=>part[0]).join('').toUpperCase()||'?';
}

export function NameCard({name,subtitle,onClick,title}){
  const content=<><span className="name-card-avatar">{initials(name)}</span><span className="name-card-copy"><span className="name-card-title">{name||'Customer'}</span>{subtitle&&<span className="name-card-subtitle">{subtitle}</span>}</span></>;
  return onClick?<button type="button" className="name-card" onClick={onClick} title={title||name}>{content}</button>:<div className="name-card" title={title||name}>{content}</div>;
}

export function Reference({children,title}){
  const value=children==null||children===''?'—':String(children);
  return <span className="reference-cell" title={title||value}>{value}</span>;
}

export function ActionMenu({items=[],label='Record actions'}){
  const[open,setOpen]=useState(false),[position,setPosition]=useState({top:0,left:0}),trigger=useRef(null),menu=useRef(null),visible=items.filter(Boolean);
  const show=event=>{
    event.stopPropagation();
    if(open){setOpen(false);return;}
    const rect=trigger.current?.getBoundingClientRect();
    if(rect)setPosition({top:Math.min(window.innerHeight-12,rect.bottom+5),left:Math.max(8,Math.min(window.innerWidth-218,rect.right-210))});
    setOpen(true);
  };
  useEffect(()=>{
    if(!open)return;
    const close=event=>{if(!trigger.current?.contains(event.target)&&!menu.current?.contains(event.target))setOpen(false);};
    const key=event=>event.key==='Escape'&&setOpen(false);
    const scroll=()=>setOpen(false);
    document.addEventListener('mousedown',close);
    document.addEventListener('keydown',key);
    window.addEventListener('resize',scroll);
    window.addEventListener('scroll',scroll,true);
    return()=>{
      document.removeEventListener('mousedown',close);
      document.removeEventListener('keydown',key);
      window.removeEventListener('resize',scroll);
      window.removeEventListener('scroll',scroll,true);
    };
  },[open]);
  return <span className="row-menu"><button ref={trigger} type="button" className="row-menu-trigger" aria-label={label} aria-expanded={open} aria-haspopup="menu" onClick={show}><MoreHorizontal size={18}/></button>{open&&createPortal(<div ref={menu} className="row-menu-popover row-menu-fixed" role="menu" style={{top:position.top,left:position.left}}>{visible.map((item,index)=>{const Icon=item.icon;return <button key={item.key||item.label||index} type="button" role="menuitem" className={item.danger?'danger':''} disabled={item.disabled} onClick={event=>{event.stopPropagation();setOpen(false);item.onClick?.();}}>{Icon&&<Icon size={15}/>}<span>{item.label}</span></button>;})}</div>,document.body)}</span>;
}

export function Table({columns,rows=[],onRow,rowActions,caption='Records'}) {
  const hasActions=typeof rowActions==='function';
  return <div className="table-wrap" role="region" aria-label={caption} tabIndex={0}>
    <table className="govuk-table">
      <caption className="govuk-table__caption cw-visually-hidden">{caption}</caption>
      <thead className="govuk-table__head"><tr className="govuk-table__row">{columns.map(column=><th className="govuk-table__header" scope="col" key={column.key}>{column.label}</th>)}{hasActions&&<th className="govuk-table__header" scope="col">Actions</th>}</tr></thead>
      <tbody className="govuk-table__body">{rows.length?rows.map((row,index)=><tr key={row.id||row.ref||index} onClick={onRow?()=>onRow(row):undefined} className={`govuk-table__row${onRow?' clickable-row':''}`}>
        {columns.map(column=><td className="govuk-table__cell" key={column.key}>{column.render?column.render(row):row[column.key]}</td>)}
        {hasActions&&<td className="row-action govuk-table__cell"><ActionMenu items={rowActions(row)||[]}/></td>}
      </tr>):<tr className="govuk-table__row"><td colSpan={columns.length+(hasActions?1:0)} className="cw-table-empty govuk-table__cell">No records to display.</td></tr>}</tbody>
    </table>
  </div>;
}

export function Breadcrumbs({items=[]}){
  return <nav className="breadcrumbs govuk-breadcrumbs" aria-label="Breadcrumb"><ol className="govuk-breadcrumbs__list">{items.map((item,index)=>{
    const label=typeof item==='string'?item:item.label||item.text||'';
    const to=typeof item==='string'?breadcrumbRoutes[item]:item.to||item.href;
    const current=index===items.length-1;
    return <li className="govuk-breadcrumbs__list-item" key={`${label}-${index}`}>{!current&&to?<Link className="govuk-breadcrumbs__link" to={to}>{label}</Link>:<span aria-current={current?'page':undefined}>{label}</span>}</li>;
  })}</ol></nav>;
}
